import {AppStatus} from "../types/signatures";
import {SignatureTypes} from "../types/pdfSignatures";
import {ENV} from "../utils/constants";

const en = {
    locale: {
        en: "EN",
        it: "IT",
    },
    user: {
        profile: "User Profile",
        account: "Account",
        logout: "Logout",
        login: "Login",
        buy: "Buy",
        intDoc: "Integrate/Documentation",
        support: "Support",
        name: "Name",
        email: "Email",
        modify: "Modify",
        save: "Save changes",
        apiKeys: {
            title: "Api Keys",
            create: "Create Api Keys",
            toStart: "For starters create your Api Keys.",
            help: "Here you can see your generated Api Keys.<br/>For security reasons there can only coexist maximun 2 Api Keys. If there are 2 Api Keys, you will need to delete one to create a new one.",
            new: "New Api Key",
            id: "Key id",
            secret: "Key secret",
            warning: "Warning! The secret key will only be visible now, copy the key to the clipboard and preserve it",
            error: "To create a new Api Key delete one of the existing ones",
            delete: "Are you sure you want to delete this API key?",
            notAllowed: "You haven't bought anything, please go to the store."
        },
        documentation: "Documentation",
        resetPasswordMessage: "An email was sent to your email address with instructions to change your password",
        resendEmail: "Resend",
        resendEmailMessage: "An email was sent to your email address with instructions to verify your account. After verifying it, reload this page",
        confirmEmail: "Please verify your email address by clicking the link we sent to the email <b>%{email, afterwards click the button bellow to proceed.",
        verified: "Email Verified",
        resendEmailLink: "Can't find the verification email? Resend it",
    },
    menu: {
        home: "Home",
        endpoints: {
            title: "Endpoints",
            pdf: "Document Simple Forensic",
            aes: "Document Advanced",
            pdfsimple: "Document Simple",
            html: "Webpage Forensic",
            data: "Data Forensic",
            timestamp: "Timestamp",
        },
        admin: {
            title: "Administration",
            apiKeys: "API Keys",
            archCall: "Archetypes and Callbacks",
            docs: "API Documentation",
            validator: "Validator",
        },
        dashboard: "Dashboard",
    },
    home: {
        payoff: "Electronic signature of Internet interactions",
        call: "Login or buy credits to continue using Web Sign.",
        contactus: "Need any help?",
        contactSubject: "Need help with Web Sign",
        contactBody: "Hello,\nWe would like some help with Web Sign,\n",
    },
    generic: {
        ok: "OK",
        yes: "Yes",
        delete: "Delete",
        cancel: "Cancel",
        no: "No",
        close: "Close",
        or: "Or",
        true: "Yes",
        false: "No",
        create: "Create",
        emptyCollection: "You haven't done anything",
        required: "Required",
        badJson: "Malformed Json",
        badUrl: "Malformed URL",
    },
    callbacks: {
        title: "User callbacks",
        create: "Create user callback",
        svix: "Inspect callbacks",
        table: {
            resourceType: "Resource Type",
            endpoint: "Endpoint",
            onlyFinalStatuses: "Only Final",
            events: "Events",
            Interaction: "Webpage/Data API",
            Signature: "Document API",
            USER_CONCLUDED: "User concluded"
        }
    },
    archetypes: {
        title: "Archetypes",
        create: "Create archetype",
        table: {
            mandatoryTags: "Madatory Tags",
            formTags: "Form Tags",
            name: "Name",
            smsCustomMessage: "SMS custom message",
            smsCustomMessageModeratedOn: "SMS moderation date",
            openCustomisations: "Customisations",
            signatureCustomisations: "Signature Customisations",
        },
        errorDeleting: "Cannot delete the archetype because it is linked to an interaction",
        hintTag: "You can enter more than one separated by ,"
    },
    input: {
        copy: "Copy",
        copied: "Copied",
    },
    error: {
        page: "Error",
        forbidden: "Access Denied",
        notFound: "Page not found",
        notAuthenticated: "Not authenticated",
        delete: "Could not delete",
    },
    interaction: {
        totalUsed: "Total signatures used",
        available: "Remaining signatures",
        overQuota: "Extra signatures used",
        consumedDocuments: "Extra documents preserved",
        tsTotalUsed: "Total timestamps used",
        tsAvailable: "Remaining timestamps",
        tsOverQuota: "Extra timestamps used",
        empty: `You have finished the prepaid API calls, from now on you will be billed for each extra call you make. If you want to change your subscription go to the <a href="https://${ENV !== "prod" ? `${ENV}.` : ""}store.kopjra.com/web-trust" target="_blank"><b>Store</b></a>.`,
        html: "Webpage",
        htmlTitle: "Webpage Forensic Signatures",
        data: "Data",
        dataTitle: "Data Forensic Signatures",
        PDF: "Document",
        pdfTitle: "Document Forensic Signatures",
        pdfSimpleTitle: "Document Simple Signatures",
        timestamp: "Timestamp",
        timestampTitle: "Timestamps",
        noTimestamps: "There are no timestamps",
        dragFiles: "Drag 'n' drop a file here or click to select file",
        doTrustedTimestamp: "Trusted Timestamp",
        doTrustlessTimestamp: "Trustless Timestamp",
        aesTitle: "Document Advanced Signatures",

        noInteraction: "There are no signatures.",
        noKeys: "To begin using the service create the Api Keys or go to the documentation.",
        withKeys: "To begin go to the documentation.",
        table: {
            id: "Id",
            createdAt: "Creation Date",
            oURL: "oUrl",
            currentStatus: "Status",
            userLandedAt: "User landed",
            userTookoffAt: "User took off",
            signerFullname: "Signer name",
            signatureType: "Signature Type",
            otpSms: "Otp sms",
            appStatus: {
                [AppStatus.ONGOING]: "On going",
                [AppStatus.FAILED]: "Failed",
                [AppStatus.CONCLUDED]: "Concluded",
            },
            signatureTypes: {
                [SignatureTypes.GRAPHIC]: "Graphic stroke",
                [SignatureTypes.NONE]: "Empty stroke",
                [SignatureTypes.FULLNAME_DATE]: "Name auto stroke",
                [SignatureTypes.INITIALS_DATE]: "Initials auto stroke",
            },
            trusted: "Trusted",
            timestampRequestedAt: "Request Date",
            download: "Download",
            downloadXML: "XML",
            downloadPDF: "PDF",
            details: "Details",
            filter: {
                id: "ID",
                oUrl: "oURL",
                signerFullname: "Signer name",
                signatureType: "Signature Type",
                otpSms: "Otp sms",
                currentStatus: "Status",
                tagKey: "Tag Key",
                tagValue: "Tag Value",
                fileName: "File Name",
                trusted: "Trusted",
            },
        },
    },
    kptable: {
        filters: "Filters",
        apply: "Apply",
        show_columns: "Show columns",
        pager: {
            first: "First",
            last: "Last",
        },
        actions: "Actions",
        date: {
            short: "MM/DD/YYYY",
            long: "MM/DD/YYYY hh:mm a",
        },
        confirm: {
            yes: "Yes",
            no: "No",
        },
        show: "Show",
        globalSelectionWarn: "This selection only applies for the %{count} visible elements",
    },
};
export default en;
